import axios, {
  AxiosResponse,
  AxiosResponseHeaders,
  InternalAxiosRequestConfig,
  RawAxiosResponseHeaders,
} from 'axios'

import { CUSTOM_EVENT } from '@/lib/constants'
import { getApiUrl } from '@/lib/helpers/apiHelpers'
import { currentUrl } from '@/lib/helpers/routeHelpers'
import { ROUTES } from '@/lib/routes'
import authService from '@/lib/services/authService'
import errorTypeService from '@/lib/services/errorTypeService'
import sentryService from '@/lib/services/sentryService'
import sessionContextService from '@/lib/services/sessionContextService'

let isEmbed = false

const apiService = axios.create({
  baseURL: getApiUrl(),
  // True by default, but we may override later
  withCredentials: true,
  transitional: {
    // throw ETIMEDOUT error instead of generic ECONNABORTED on request timeouts
    clarifyTimeoutError: true,
  },
})

// CSRF protection: the API requires this custom header on every unsafe
// cookie-authenticated request. A custom header cannot be set by a cross-site
// form/simple request and forces a CORS preflight that only allowlisted origins
// pass, so forged cross-site writes are rejected. See CsrfHeaderSubscriber (API).
apiService.defaults.headers.common['X-CSRF-Protection'] = '1'

function setRefererPathHeader(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!config.headers) {
    return config
  }

  config.headers['X-Referer-Path'] = (
    window.location.pathname + window.location.search
  ).substr(1)

  return config
}

function setSessionContextHintHeader(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  const contextHint = sessionContextService.getContextHint()

  if (config.headers && contextHint) {
    config.headers['X-Session-Context-Hint'] = contextHint
  }

  return config
}

export function setUpAccessTokenForEmbed(
  accessToken: string,
  newIsEmbed: boolean,
) {
  apiService.interceptors.request.use((config) => {
    // Auth header
    config.headers.Authorization = `Bearer ${accessToken}`

    // Don't use cookies
    config.withCredentials = false

    // Make sure we know for later that we're in an embed (either the iframe, or the embedded content)
    isEmbed = newIsEmbed

    return config
  })
}

function handleRefreshExpiryHeader(
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
) {
  if (headers['x-refresh-token-expires-in']) {
    const secondsRemaining = parseInt(headers['x-refresh-token-expires-in'], 10)
    if (!isNaN(secondsRemaining)) {
      return authService.dispatchSessionExtended(secondsRemaining)
    }
  }

  // If no refresh token expiry set we can assume the user isn't logged in anymore
  authService.dispatchSessionIsAuthenticated(false)
}

// The context-switching endpoints legitimately change the session context as a
// side effect, so a mismatch against the (pre-switch) baseline is expected there
// and must not trigger a logout.
function isContextMutatingRequest(
  config?: InternalAxiosRequestConfig,
): boolean {
  const url = config?.url ?? ''

  return url.includes('/session-metadata') || /\/users\/\d+\/context/.test(url)
}

/**
 * When the API reports the session is now acting as a different
 * user/scope/company/group than the frontend was initialised with, warn the
 * user (the conflict modal, which then re-bootstraps the app against the actual
 * server context by reloading). Returns true when handled so callers can stop
 * further handling.
 */
function handleSessionContext(
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
  config?: InternalAxiosRequestConfig,
): boolean {
  if (isEmbed || isContextMutatingRequest(config)) {
    return false
  }

  if (!sessionContextService.hasUnexpectedContextSwitch(headers)) {
    return false
  }

  console.log('Session context changed unexpectedly; showing conflict warning.')
  authService.dispatchSessionContextConflict()

  return true
}

function handleErrors(error: unknown) {
  if (isEmbed) {
    // We handle these differently
    return Promise.reject(error)
  }

  if (
    axios.isAxiosError(error) &&
    error.response?.headers &&
    handleSessionContext(error.response.headers, error.config)
  ) {
    return
  }

  if (errorTypeService.isTokenError(error)) {
    authService.logout()
    return
  }

  if (errorTypeService.isExpiredUserContextError(error)) {
    console.log('Reloading the page to refresh the user context.')
    window.location.reload()
    return
  }

  if (errorTypeService.isCloudflareChallengeResponse(error)) {
    window.location.assign(
      ROUTES.challenge({ searchParams: { redirectUri: currentUrl() } }),
    )
    return
  }

  if (errorTypeService.isTooManyRequestsError(error)) {
    // Tell RateLimitedWarning to show a toast. The error is still rejected
    // below so the caller can reset its own loading state.
    window.dispatchEvent(
      new CustomEvent<number>(CUSTOM_EVENT.rateLimited, {
        detail: Number(error.response?.headers['retry-after']) || 0,
      }),
    )
  }

  if (axios.isAxiosError(error) && error.response?.headers) {
    // Handle session expiry update from API error responses
    handleRefreshExpiryHeader(error.response.headers)
  }

  sentryService.captureApiError(error)

  // Don't handle any other error types
  return Promise.reject(error)
}

function handleResponse(response: AxiosResponse) {
  const headers = response.headers

  if (handleSessionContext(headers, response.config)) {
    return response
  }

  if (headers['x-company-outstanding-payments']) {
    window.dispatchEvent(
      new CustomEvent<string>('outstandingPayments', {
        detail: headers['x-company-outstanding-payments'],
      }),
    )
  }

  // Handle session expiry from API responses
  handleRefreshExpiryHeader(headers)

  return response
}

apiService.interceptors.request.use(setRefererPathHeader)
apiService.interceptors.request.use(setSessionContextHintHeader)
apiService.interceptors.response.use(
  // Any status code within the 2xx range will trigger this function
  (response) => handleResponse(response),

  // Any status codes outside the 2xx range will trigger this function
  (error) => handleErrors(error),
)

export default apiService
